import IconButton from '@mui/material/IconButton';
import styled from 'styled-components';
import { Colors } from 'styles/index';
import SendIcon from '@mui/icons-material/Send';
import { useCallback, useEffect, useLayoutEffect, useRef, useMemo } from 'react';
import { isEmptyStringOrNull } from '@sironamedical/common';
import { rootTheme } from 'theme/rootTheme';

type ComposerProps = {
  comment: string;
  setComment: (comment: string) => void;
  onSend: () => Promise<void>;
  maxHeight?: number;
  minHeight?: number;
  showSendButton?: boolean;
};

const DEFAULT_MAX = 300;
const DEFAULT_MIN = 40;

export const CommentBox = styled.div`
  box-sizing: border-box;
  display: flex;
  flex-direction: column-reverse;
  align-items: stretch;
  position: relative;
  width: 100%;
  background-color: ${Colors.gray1};
  border: 1px solid ${Colors.gray4};
  border-radius: ${rootTheme.radius.md}px;
  padding: 4px 8px;
  position: relative;

  &:focus {
    border: 1px solid ${Colors.gray10};
    outline: none;
  }
`;

export const ExamCommentTextArea = styled.textarea<{ $maxHeight: number; $minHeight: number }>`
  border-radius: ${rootTheme.radius.md}px;
  background-color: ${Colors.gray1};
  border: none;
  outline: none;
  padding: 8px 16px 8px 4px;
  resize: none;
  font-family: 'Roboto', sans-serif;
  font-size: 14px;
  width: 100%;
  min-height: ${(p) => p.$minHeight}px;
  max-height: ${(p) => p.$maxHeight}px;
  overflow-y: hidden;

  &:focus {
    border: none;
    outline: none;
  }
`;

export const ExamCommentComposer = ({
  comment,
  setComment,
  onSend,
  maxHeight = DEFAULT_MAX,
  minHeight = DEFAULT_MIN,
  showSendButton = true,
}: ComposerProps) => {
  const textAreaRef = useRef<HTMLTextAreaElement>(null);
  const boxRef = useRef<HTMLDivElement>(null);
  const prevBoxHeightRef = useRef<number>(0);

  const isSendingRef = useRef(false);

  const reportBoxHeight = useCallback(() => {
    if (!boxRef.current) return;
    const h = boxRef.current.offsetHeight;
    if (h !== prevBoxHeightRef.current) {
      prevBoxHeightRef.current = h;
    }
  }, []);

  const autoResize = useCallback(() => {
    const el = textAreaRef.current;
    if (!el) return;

    el.style.height = '0px';
    const next = Math.max(minHeight, Math.min(el.scrollHeight, maxHeight));
    el.style.height = `${next}px`;
    el.style.overflowY = next >= maxHeight ? 'auto' : 'hidden';

    requestAnimationFrame(reportBoxHeight);
  }, [maxHeight, minHeight, reportBoxHeight]);

  useLayoutEffect(() => {
    autoResize();
  }, [autoResize, reportBoxHeight]);

  useEffect(() => {
    autoResize();
  }, [comment, autoResize]);

  const isCommentEmpty = useMemo(() => isEmptyStringOrNull(comment?.trim()), [comment]);

  return (
    <CommentBox ref={boxRef}>
      <ExamCommentTextArea
        ref={textAreaRef}
        placeholder="Add a comment"
        value={comment}
        $maxHeight={maxHeight}
        $minHeight={minHeight}
        onChange={(e) => setComment(e.target.value)}
        onKeyDown={async (e) => {
          if (comment == null || isCommentEmpty) {
            return;
          }

          if (isSendingRef.current !== true && showSendButton && e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();

            isSendingRef.current = true;
            await onSend();
            isSendingRef.current = false;
          }
        }}
      />
      {showSendButton && !isCommentEmpty && (
        <IconButton
          sx={{
            position: 'absolute',
            bottom: 0,
            right: 0,

            '&:hover': {
              background: 'none',
            },
          }}
          disableFocusRipple
          disableRipple
          title="Send"
          onClick={async () => {
            if (isSendingRef.current !== true) {
              isSendingRef.current = true;
              await onSend();
              isSendingRef.current = false;
            }
          }}
          size="large"
        >
          <SendIcon
            sx={{
              width: 18,
              height: 16,
              color: Colors.blue4,
            }}
          />
        </IconButton>
      )}
    </CommentBox>
  );
};
